import React, { useEffect, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import { FormGroup, InputGroup, Tab, Tabs } from 'react-bootstrap';
import Button from 'react-bootstrap/Button';
import _ from 'lodash';
import HttpMethodDropdown from 'components/buttons/HttpMethodDropdown';
import i18n from 'i18n';
import BzFormControlWithHook from 'components/forms/BzFormControlWithHook';
import { CallbackFn, useFormInput, Validator, ValidatorType } from 'hooks/useFormInput';
import RadioButton from 'components/buttons/RadioButton';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import { convertToNumber } from 'utils/commonUtil';
import Form from 'react-bootstrap/Form';
import { useIsMount } from 'hooks/useIsMountHook';
import Autofocus from 'components/forms/Autofocus/Autofocus';
import { showToastNotification } from 'store/ui/toasts/toastsActions';
import { ToastType } from 'store/ui/toasts/toastsTypes';
import { useDispatch } from 'react-redux';
import { TriggerTime } from 'views/AssetCatalog/Transactions/TransactionEditor/ProcessingActions/ProcessingActionModal';
import { ProcessingAction, Webhook } from '../../../../../../types/rest/assetCatalogTypes';
import { saveProcessingAction } from '../CommonActions';
import ProcessingActionConditions from './ProcessingActionConditions';
import { Condition } from '../../../../../../types/rest/dslTypes';
import PairSetList, { PairSetItem } from '../../../../../../components/pairSet/PairSetList';
import SslConfiguration, { SSlConfigMode } from 'views/MockServices/Tabs/MockServices/SslConfiguration';
import { AssetAssignmentBundle } from '../../../../../MockServices/Tabs/MockServices/SslConfiguration';

type HttpRequestModalProps = {
  processingAction: ProcessingAction;
  onClose: () => void;
  onCreateFinished: () => void;
  txConditions?: Condition[];
};

const HttpRequestDict = {
  WEBHOOK: {
    title: 'transactions:processingActions.modal.webHook',
    desc: 'transactions:processingActions.modal.description',
    enterName: 'transactions:processingActions.modal.enterName',
    error: 'transactions:processingActions.modal.error.saveEdit',
    url: 'transactions:processingActions.modal.callBackUrl',
    fieldHint: 'transactions:processingActions.modal.callBackUrl',
    body: 'transactions:processingActions.modal.callBackBody',
    enterBody: 'transactions:processingActions.modal.enterCallBackBody',
    validationMsg: 'transactions:processingActions.modal.validation.invalidChars',
    nameRequired: 'transactions:processingActions.modal.webhook.nameRequired',
  },
  HTTP_CALL: {
    title: 'transactions:processingActions.modal.httpCallModal.title',
    desc: 'transactions:processingActions.modal.httpCallModal.description',
    enterName: 'transactions:processingActions.modal.httpCallModal.enterName',
    error: 'transactions:processingActions.modal.httpCallModal.error',
    url: 'transactions:processingActions.modal.httpUrl',
    fieldHint: 'transactions:processingActions.modal.enterUrl',
    body: 'transactions:processingActions.modal.httpBody',
    enterBody: 'transactions:processingActions.modal.enterBody',
    validationMsg: 'transactions:processingActions.modal.validation.invalidChars',
    nameRequired: 'transactions:processingActions.modal.httpCallModal.nameRequired',
  },
};

const HttpRequestModal = (props: HttpRequestModalProps) => {
  const dispatch = useDispatch();
  const { actionType } = props.processingAction;
  const [isValid, setIsValid] = useState(false);
  const [processingAction, setProcessingAction] = useState(props.processingAction);

  //TODO - remove this state, it is a duplicate
  const [definition, setDefinition] = useState(props.processingAction.definition as Webhook);

  const urlValidators: Validator[] = [
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('transactions:editor.urlRequired'),
    },
    {
      type: ValidatorType.INVALID_LEADING_SPACES,
      invalidMessage: i18n.t('transactions:editor.urlIsInvalid'),
    },
  ];

  const nameValidators: Validator[] = [
    {
      type: ValidatorType.INVALID_CHARACTERS,
      invalidMessage: i18n.t(HttpRequestDict[actionType].validationMsg),
      value: /^(?![0-9])[a-zA-Z0-9$_]+$/,
    },
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t(HttpRequestDict[actionType].nameRequired),
    },
    {
      type: ValidatorType.MAX_LENGTH,
      value: 250,
      invalidMessage: i18n.t('transactions:processingActions.modal.validation.nameMaxLength'),
    },
  ];

  const urlInput = useFormInput(
    (props.processingAction.definition as Webhook).urlValue
      ? (props.processingAction.definition as Webhook).urlValue
      : '',
    (fn: CallbackFn) => {
      updateDefinition('urlValue', fn.value);
    },
    ...urlValidators,
  );

  const nameInput = useFormInput(
    processingAction.name ? processingAction.name : '',
    (fn: CallbackFn) => {
      if (fn.isValid) {
        onChangeName(fn.value as string);
      }
    },
    ...nameValidators,
  );

  const onChangeName = (value: string) => {
    const current = _.cloneDeep(definition);
    setIsValid(isCurrentModalValid(current));
    setDefinition(current);
    setProcessingAction({ ...processingAction, name: value, definition: current });
  };

  const updateDefinition = (field: string, value: any) => {
    const current = _.cloneDeep(definition);
    _.set(current, field, value);
    setIsValid(isCurrentModalValid(current));
    setDefinition(current);
    setProcessingAction({ ...processingAction, definition: current });
  };
  const validateCurrentModel = () => {
    setIsValid(isCurrentModalValid({ ...definition }));
  };

  const isCurrentModalValid = (currentDef: any): boolean => {
    let isValid = specificInput.valid;
    if (currentDef.triggerTime === TriggerTime.RANDOM) {
      isValid = lowerInput.valid && upperInput.valid;
    }
    return isValid && urlInput.valid && nameInput.valid;
  };

  const onSave = async () => {
    try {
      await saveProcessingAction(processingAction, actionType);
    } catch (e) {
      const errorBody = HttpRequestDict[actionType].error;
      dispatch(
        showToastNotification(
          ToastType.Danger,
          i18n.t('errorMsgs.unexpectedError'),
          i18n.t(errorBody, {
            reason: e.response ? e.response.data.error : 'Something went wrong.',
          }),
        ),
      );
    }
    props.onClose();
    props.onCreateFinished();
  };

  const isMount = useIsMount();

  const validateRandomRange = (type: 'lower' | 'upper') => {
    //Cross field validation fails
    if (definition.lower > definition.upper) {
      const validation = {
        invalidMessage: i18n.t('mockServices:parametersTab.thinkTimeValidation.lowerUpperValidation'),
        isValid: false,
      };
      if (type === 'lower') {
        lowerInput.overrideValidation(validation);
      } else {
        upperInput.overrideValidation(validation);
      }
    }
    // Both values are valid, we can propagate them and reset validation
    else {
      const validation = { invalidMessage: '', isValid: true };
      lowerInput.overrideValidation(validation);
      upperInput.overrideValidation(validation);
    }
  };

  useEffect(() => {
    if (!isMount) {
      validateRandomRange('lower');
    }
  }, [definition.lower]);

  useEffect(() => {
    if (!isMount) {
      validateRandomRange('upper');
    }
  }, [definition.upper]);

  const timeValidators: Validator[] = [
    {
      type: ValidatorType.REQUIRED,
      invalidMessage: i18n.t('transactions:processingActions.modal.validation.required'),
    },
    {
      type: ValidatorType.INTEGER,
      invalidMessage: i18n.t('transactions:processingActions.modal.validation.numberValidation'),
    },
  ];
  const lowerInput = useFormInput(
    definition.lower,
    fn => {
      if (fn.isValid) {
        updateDefinition('lower', convertToNumber(fn.value));
      } else {
        updateDefinition('isValid', false);
      }
    },
    ...timeValidators,
  );

  const upperInput = useFormInput(
    definition.upper,
    fn => {
      if (fn.isValid) {
        updateDefinition('upper', convertToNumber(fn.value));
      } else {
        updateDefinition('isValid', false);
      }
    },
    ...timeValidators,
  );

  const specificInput = useFormInput(
    definition.specific,
    fn => {
      if (fn.isValid) {
        updateDefinition('specific', convertToNumber(fn.value));
      } else {
        updateDefinition('isValid', false);
      }
    },
    ...[
      ...timeValidators,
      {
        type: ValidatorType.MAX,
        value: 999999999,
        invalidMessage: i18n.t('transactions:processingActions.modal.validation.numberValidationRange'),
      },
    ],
  );

  const handleChange = (e: any) => {
    const target = e.target as HTMLInputElement;
    updateDefinition(target.name, target.value);
  };

  const [tabsState, setTabsState] = useState('body');

  const gapBetweenRadios = 'mb-3';
  const gapInExpandedDiv = 'mb-3';

  const thinkTimeSettings = () => (
    <>
      <div className={'mt-5'}>
        <label> {i18n.t('transactions:processingActions.modal.triggerTitle')}</label>
        <FormGroup id={'think-time'} className={'ml-1'}>
          <div className={gapBetweenRadios}>
            <RadioButton
              isChecked={definition.triggerTime === TriggerTime.SPECIFIC}
              id={'specific'}
              label={i18n.t('transactions:processingActions.modal.specific')}
              onChange={() => updateDefinition('triggerTime', TriggerTime.SPECIFIC)}
            ></RadioButton>
            <div className={gapInExpandedDiv}>
              {definition.triggerTime === TriggerTime.SPECIFIC && (
                <Row>
                  <Col md={4}>
                    <InputGroup>
                      <BzFormControlWithHook
                        useFormInputHook={specificInput}
                        placeholder={i18n.t('common:thinkTime.placeholders.fixed')}
                        required={false}
                        hideErrors={true}
                        autoComplete={'off'}
                      />
                      <InputGroup.Append>
                        <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                      </InputGroup.Append>
                      <div className="invalid-feedback">{specificInput.invalidMessage}</div>
                    </InputGroup>
                  </Col>
                </Row>
              )}
            </div>
          </div>
          <div className={gapBetweenRadios}>
            <RadioButton
              isChecked={definition.triggerTime === TriggerTime.RANDOM}
              id={'random'}
              label={i18n.t('transactions:processingActions.modal.random')}
              onChange={() => updateDefinition('triggerTime', TriggerTime.RANDOM)}
            ></RadioButton>
            <div className={gapInExpandedDiv}>
              {definition.triggerTime === TriggerTime.RANDOM && (
                <Row>
                  <Col>
                    <InputGroup>
                      <BzFormControlWithHook
                        id={'lowerInput'}
                        useFormInputHook={lowerInput}
                        placeholder={i18n.t('common:thinkTime.placeholders.lower')}
                        required={false}
                        hideErrors={true}
                        autoComplete={'off'}
                      />
                      <InputGroup.Append>
                        <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                      </InputGroup.Append>
                      <div className="invalid-feedback">{lowerInput.invalidMessage}</div>
                    </InputGroup>
                    <p
                      className="text-muted small"
                      dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.lower') }}
                    ></p>
                  </Col>
                  <Col>
                    <InputGroup>
                      <BzFormControlWithHook
                        id={'upperInput'}
                        useFormInputHook={upperInput}
                        placeholder={i18n.t('common:thinkTime.placeholders.upper')}
                        required={false}
                        hideErrors={true}
                        autoComplete={'off'}
                      />
                      <InputGroup.Append>
                        <InputGroup.Text>{i18n.t('common:thinkTime.millisSeconds')}</InputGroup.Text>
                      </InputGroup.Append>
                      <div className="invalid-feedback">{upperInput.invalidMessage}</div>
                    </InputGroup>
                    <p
                      className="text-muted small"
                      dangerouslySetInnerHTML={{ __html: i18n.t('common:thinkTime.upper') }}
                    ></p>
                  </Col>
                  <Col></Col>
                </Row>
              )}
            </div>
          </div>
        </FormGroup>
      </div>
    </>
  );

  const modalHeader = () => {
    return (
      <Modal.Header closeButton id="qa-modal-assign-template-btn-close-icon" style={{ marginBottom: '-15px' }}>
        <h5 className="modal-title">{i18n.t(HttpRequestDict[actionType].title)}</h5>
      </Modal.Header>
    );
  };

  const onUpdateConditionsHandler = (conditions: Condition[]) => {
    validateCurrentModel();
    setProcessingAction({ ...processingAction, conditions: conditions });
  };

  const buildAssetBundle = (processingAction: ProcessingAction) => {
    const bundle: AssetAssignmentBundle = {
      assets: processingAction.assets,
      configKeyAssets: processingAction.configKeyAssets,
    };
    return bundle;
  };

  return (
    <Modal
      show={true}
      onHide={props.onClose}
      enforceFocus={false}
      animation={false}
      backdropClassName={'backdrop-faded'}
    >
      {modalHeader()}

      <Modal.Body>
        <p>{i18n.t(HttpRequestDict[actionType].desc)}</p>
        <label htmlFor="webhookName">{i18n.t('transactions:processingActions.modal.name')}</label>

        {/* Name */}
        <Autofocus>
          <BzFormControlWithHook
            id={'webhookName'}
            useFormInputHook={nameInput}
            placeholder={i18n.t(HttpRequestDict[actionType].enterName)}
            required={true}
            hideErrors={false}
            autoComplete={'off'}
          />
        </Autofocus>
        {/* ./Name */}

        {/* Think Time */}
        {processingAction.actionType === 'WEBHOOK' && thinkTimeSettings()}
        {/* ./Think Time */}

        <hr className={'mt-4'} />

        {/* Conditions */}
        <ProcessingActionConditions
          onUpdateConditions={onUpdateConditionsHandler}
          allConditions={props.txConditions}
          conditions={processingAction.conditions}
        />
        {/* ./Conditions */}

        {/* Callback URL */}
        <div className={'mt-4'}>
          <label htmlFor="input-url">{i18n.t(HttpRequestDict[actionType].url)}</label>
          <InputGroup className={'mb-5'}>
            <HttpMethodDropdown
              isReadOnly={false}
              method={definition.urlMethod}
              updateState={(field: string, value: any) => {
                updateDefinition('urlMethod', value);
              }}
            />
            <BzFormControlWithHook
              useFormInputHook={urlInput}
              autoComplete={'off'}
              type="text"
              name="url.matchingValue"
              placeholder={i18n.t(HttpRequestDict[actionType].fieldHint)}
              required={true}
              id="input-url"
              hideErrors={false}
            />
          </InputGroup>
        </div>
        {/* ./ Callback URL */}

        {/* SSL Config */}
        <SslConfiguration
          onAssignmentChanged={(assetBundle: AssetAssignmentBundle) => {
            validateCurrentModel();
            setProcessingAction({
              ...processingAction,
              assets: assetBundle.assets,
              configKeyAssets: assetBundle.configKeyAssets,
            });
          }}
          assetsBundle={buildAssetBundle(processingAction)}
          disabled={false}
          heading={<label>{i18n.t('common:sslConfig.title')}</label>}
          configMode={SSlConfigMode.CLIENT}
        />
        {/* ./ SSL Config */}

        <Tabs
          transition={false}
          id="service-mock-tabs"
          activeKey={tabsState}
          onSelect={setTabsState}
          mountOnEnter={true}
        >
          <Tab eventKey="queryParams" title={'Query Parameters'}>
            <PairSetList
              onListChange={(items: PairSetItem[]) => updateDefinition('queryParams', items)}
              pairList={definition.queryParams}
              id="pairs-query-params"
              emptyListMessage={i18n.t('transactions:processingActions.modal.emptyQueryListMessage')}
              nonEmptyListMessage={i18n.t('transactions:processingActions.modal.nonEmptyQueryListMessage')}
              pairTitle={i18n.t('transactions:processingActions.modal.queryMatcherTitle')}
            />
          </Tab>
          <Tab eventKey="header" title={'Header'}>
            <PairSetList
              onListChange={(items: PairSetItem[]) => updateDefinition('headers', items)}
              pairList={definition.headers}
              id="pairs-header"
              emptyListMessage={i18n.t('transactions:processingActions.modal.emptyHeaderListMessage')}
              nonEmptyListMessage={i18n.t('transactions:processingActions.modal.nonEmptyHeaderListMessage')}
              pairTitle={i18n.t('transactions:processingActions.modal.headerMatcherTitle')}
            />
          </Tab>
          <Tab eventKey="body" title={'Body'}>
            <Form.Group controlId={'textarea-body-matcher'} className={'w-auto'}>
              <label className={'font-size-lg font-weight-semi-bold'}>{i18n.t(HttpRequestDict[actionType].body)}</label>
              <Form.Control
                autoComplete={'off'}
                as="textarea"
                rows={5}
                name="bodyContent"
                onChange={handleChange}
                placeholder={i18n.t(HttpRequestDict[actionType].enterBody)}
                value={definition.bodyContent}
              />
            </Form.Group>
          </Tab>
        </Tabs>
      </Modal.Body>

      <Modal.Footer>
        <Button variant={'link'} onClick={props.onClose}>
          {i18n.t('transactions:processingActions.modal.cancel')}
        </Button>
        <Button disabled={isValid === false} onClick={onSave}>
          {i18n.t('transactions:processingActions.modal.save')}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default HttpRequestModal;
